/*
 * Copyright (c) 2022 CalAmp Corp.  All Rights Reserved
 */
import {useState} from 'react';
import {Search, SearchCriteria} from '@ion/core/src/types/services';
import {FilterChips} from '@ion/dls/src/types/editors';

export interface UseGridSelectionProps {
  areTotalRowsSelected?: boolean;
  totalRowsCount?: number;
  filters?: FilterChips;
  search?: Search | SearchCriteria;
  includeSubAccounts?: boolean;
};

/**
 * React Hook to provide local selector/state for grid selection functionality.
 *
 *  ```typescript
 *   import {useGridSelection} from '@ion/applications/src/hooks/';
 *
 *   const {
 *     areTotalRowsSelected,
 *     setAreTotalRowsSelected,
 *     totalRowsCount,
 *     setTotalRowsCount,
 *     filters,
 *     setFilters,
 *     search,
 *     setSearch,
 *     includeSubAccounts,
 *     setIncludeSubAccounts
 *   } = useGridSelection();
 *  ```
 */
export const useGridSelection = ({
  areTotalRowsSelected : originalAreTotalRowsSelected,
  totalRowsCount       : originalTotalRowsCount,
  filters              : originalFilters,
  search               : originalSearch,
  includeSubAccounts   : originalIncludeSubAccounts
}: UseGridSelectionProps) => {
  const [areTotalRowsSelected, setAreTotalRowsSelected] = useState<boolean>(originalAreTotalRowsSelected || false);
  const [totalRowsCount, setTotalRowsCount]             = useState<number>(originalTotalRowsCount || 0);
  const [filters, setFilters]                           = useState<FilterChips>(originalFilters || []);
  const [search, setSearch]                             = useState<Search | SearchCriteria>(originalSearch);
  const [includeSubAccounts, setIncludeSubAccounts]     = useState<boolean>(originalIncludeSubAccounts || false);

  return {
    areTotalRowsSelected,
    setAreTotalRowsSelected,
    totalRowsCount,
    setTotalRowsCount,
    filters,
    setFilters,
    search,
    setSearch,
    includeSubAccounts,
    setIncludeSubAccounts
  };
};

export default useGridSelection;
